/**
 * « Mode d'emploi » du formateur (lot 1.2) — repris de la page « Instructions » du premier SaaS, adapté au nouveau :
 * le parcours pas à pas, la règle de rémunération LUE dans la configuration de l'organisme (jamais en dur ici),
 * les sept étapes du dossier, et ce qu'il faut avant la demande de paiement.
 * Chaque section porte une ancre : les liens « Aide » des titres de page (`TitrePage`, prop `aide`) y mènent directement.
 */
import { useEffect } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, Banknote, BookOpen, ClipboardSignature, FilePen, FolderCheck, GraduationCap, IdCard, ShieldCheck, UsersRound } from "lucide-react";
import { ETAPES } from "@/domaine/pipeline/statuts";
import { NOMENCLATURE } from "@/domaine/referentiel/pieces";
import { api, euros, type LigneVeille, type ReglesPortage } from "../api";
import { FicheVeille } from "./Veille";
import { Carte, cx, TitrePage } from "../ui/base";

const PARCOURS = [
  { icone: IdCard, titre: "Complétez votre profil", texte: "Photo, identité, structure et SIRET (rempli depuis l'annuaire officiel), NDA (ou « Je n'ai pas de NDA »), domaine d'expertise, profil LinkedIn. Dans « Mes pièces » : CV, parcours de formation, déroulé pédagogique et récépissé de NDA. Ces informations alimentent tous vos dossiers.", vers: "/profil", lien: "Mon profil" },
  { icone: BookOpen, titre: "Créez vos formations", texte: "Objectifs, durée, tarif, programme. L'assistant IA se documente sur le sujet et vous propose un parcours, des tests et des supports : vous relisez, vous restez l'auteur.", vers: "/formations", lien: "Mes formations" },
  { icone: UsersRound, titre: "Enregistrez apprenants et entreprises", texte: "Une fiche par personne et par entreprise, réutilisable d'un dossier à l'autre. Tapez le SIRET : raison sociale et adresse se remplissent.", vers: "/repertoire", lien: "Apprenants et entreprises" },
  { icone: ClipboardSignature, titre: "Faites positionner l'apprenant", texte: "Il reçoit un lien personnel : recueil des besoins et test de positionnement, signés en ligne. Les deux sont exigés avant de demander la validation.", vers: "/positionnements", lien: "Positionnements" },
  { icone: FilePen, titre: "Générez la convention", texte: "Apprenant, entreprise, formation, modalité, financement : cinq onglets dans l'ordre, que vous pouvez reprendre. Un numéro ADF est attribué au dossier.", vers: "/dossiers/nouveau", lien: "Générateur de conventions" },
  { icone: FolderCheck, titre: "Demandez la validation", texte: "Quand rien ne manque, « Demander la validation à l'organisme ». L'organisme contrôle, valide : les pièces de départ sont générées et l'entreprise les reçoit pour sa demande de financement.", vers: "/dossiers", lien: "Mes dossiers" },
  { icone: GraduationCap, titre: "Animez la formation", texte: "À l'accord du financeur, l'ordre de mission vous est envoyé. Convocations, émargement par séance, évaluation des acquis et satisfaction partent au bon moment.", vers: "/dossiers", lien: "Mes dossiers" },
  { icone: Banknote, titre: "Soyez payé", texte: "Formation terminée → vous déposez votre facture sur le dossier → dossier complet : l'organisme émet la demande de paiement → paiement reçu → vous êtes payé et le dossier est archivé.", vers: "/dossiers", lien: "Mes dossiers" },
] as const;

/** Les pièces exigées pour un dossier « complet », lues dans le référentiel (aucune liste recopiée à la main). */
const PIECES_REQUISES = NOMENCLATURE.filter((p) => p.requisePourCompletude).map((p) => p.libelle);

const QUI_AGIT: Record<string, string> = {
  A: "Vous créez le brouillon, joignez recueil et positionnement, puis demandez la validation. L'organisme valide ou vous renvoie le dossier avec un motif.",
  B: "L'entreprise ou l'apprenant dépose la demande auprès du financeur. L'accord, une fois déposé sur le dossier, déclenche votre ordre de mission.",
  C: "Vous envoyez les éléments pédagogiques, puis démarrez la formation : émargement à chaque séance.",
  D: "Vous déclarez la formation terminée et déposez votre facture. Le dossier est « complet » quand toutes les pièces attendues sont validées.",
  E: "Dossier complet et votre facture reçue, l'organisme émet sa facture et la demande de paiement au financeur.",
  F: "Le financeur a payé l'organisme.",
  G: "Le financement encaissé, vous êtes payé ; le dossier passe en lecture seule.",
};

function Section({ id, titre, children, className }: { id: string; titre: string; children: React.ReactNode; className?: string }) {
  return (
    <section id={id} aria-labelledby={`titre-${id}`} className={cx("scroll-mt-24", className)}>
      <h2 id={`titre-${id}`} className="mb-3 text-lg font-semibold">
        {titre}
      </h2>
      {children}
    </section>
  );
}

export function ModeEmploi() {
  const regles = useQuery({ queryKey: ["mode-emploi"], queryFn: () => api.get<ReglesPortage>("/mode-emploi") });
  const { hash } = useLocation();
  // Arrivée par un lien « Aide » : on amène la section demandée sous les yeux (et le focus clavier avec elle).
  useEffect(() => {
    if (!hash) return;
    const cible = document.getElementById(hash.replace(/^#/, ""));
    if (!cible) return;
    cible.scrollIntoView({ block: "start" });
    cible.querySelector("h2")?.setAttribute("tabindex", "-1");
    cible.querySelector<HTMLElement>("h2")?.focus({ preventScroll: true });
  }, [hash, regles.isSuccess]);

  const r = regles.data;
  return (
    <div className="mx-auto max-w-5xl">
      <TitrePage titre="Mode d'emploi" soustitre="Le portage pas à pas : ce que vous faites, ce que fait l'organisme, et comment vous êtes rémunéré." />

      <nav aria-label="Sommaire du mode d'emploi" className="mb-8 flex flex-wrap gap-2 text-sm">
        {[
          ["parcours", "Le parcours en 8 étapes"],
          ["remuneration", "Votre rémunération"],
          ["pipeline", "Les étapes d'un dossier"],
          ["paiement", "Avant la demande de paiement"],
          ["generation-ia", "Générer vos documents avec l'IA"],
          ["profil", "Votre profil et vos pièces"],
          ["cpf", "Financement CPF"],
          ["qualite", "Qualiopi et traçabilité"],
        ].map(([ancre, libelle]) => (
          <Link key={ancre} to="/mode-emploi" hash={ancre} className="inline-flex min-h-9 items-center rounded-full border border-trait-fort bg-carte px-3.5 font-medium text-encre-2 hover:border-accent/60 hover:text-encre">
            {libelle}
          </Link>
        ))}
      </nav>

      <div className="space-y-10">
        <Section id="parcours" titre="Le parcours en 8 étapes">
          <ol className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {PARCOURS.map((e, i) => (
              <li key={e.titre}>
                <Carte className="flex h-full gap-4 p-4 sm:p-5">
                  <span className="grid size-10 shrink-0 place-items-center rounded-md bg-accent-doux text-accent-fort">
                    <e.icone className="size-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-[15px] font-semibold">
                      <span className="chiffres mr-1.5 text-encre-3">{i + 1}.</span>
                      {e.titre}
                    </h3>
                    <p className="mt-1 text-sm text-encre-2">{e.texte}</p>
                    <Link to={e.vers} className="mt-2 inline-block text-sm font-medium text-accent underline-offset-4 hover:underline">
                      {e.lien}
                    </Link>
                  </div>
                </Carte>
              </li>
            ))}
          </ol>
        </Section>

        <Section id="remuneration" titre="Votre rémunération">
          <Carte className="border-attente/60 bg-attente-doux/40 p-5">
            {r ? (
              <>
                <p className="text-sm">
                  {r.organisme_nom || "L'organisme"} retient une <strong>commission de portage de {String(r.commission_pourcentage).replace(".", ",")} %</strong> du prix de vente hors taxes. Le reste vous est reversé. Vous ne saisissez jamais votre rémunération : chaque dossier la calcule dans son encadré « Finances ».
                </p>
                <dl className="chiffres mt-4 grid max-w-md grid-cols-[minmax(0,1fr)_auto] gap-x-6 gap-y-1.5 text-sm">
                  <dt className="text-encre-2">Exemple : prix de vente HT</dt>
                  <dd className="text-right">{euros(r.exemple.prix_ht)}</dd>
                  <dt className="text-encre-2">Commission de portage ({String(r.commission_pourcentage).replace(".", ",")} %)</dt>
                  <dd className="text-right">− {euros(r.exemple.commission)}</dd>
                  <dt className="border-t border-attente/60 pt-1.5 font-semibold">Net reversé au formateur</dt>
                  <dd className="border-t border-attente/60 pt-1.5 text-right font-semibold">{euros(r.exemple.net)}</dd>
                </dl>
                <p className="mt-4 text-[13px] text-encre-2">Montants hors taxes. Déposez votre facture dès la fin de la formation : l'organisme en a besoin pour demander le paiement au financeur. Vous êtes payé une fois ce financement encaissé (étape G).</p>
              </>
            ) : (
              <p className="text-sm text-encre-2">{regles.error ? regles.error.message : "Chargement de la règle de l'organisme…"}</p>
            )}
          </Carte>
        </Section>

        <Section id="pipeline" titre="Les étapes d'un dossier">
          <p className="mb-3 max-w-[70ch] text-sm text-encre-2">Chaque dossier avance de gauche à droite dans « Mes dossiers ». Un bouton n'apparaît que si l'action vous revient ; s'il est grisé, la raison est écrite juste en dessous.</p>
          <ol className="divide-y divide-trait overflow-hidden rounded-md border border-trait bg-carte">
            {ETAPES.map((e) => (
              <li key={e.cle} className="flex gap-4 px-4 py-3 sm:px-5">
                <span className="chiffres grid size-8 shrink-0 place-items-center rounded-full bg-papier-3 text-sm font-semibold text-encre-2" aria-hidden>
                  {e.cle}
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold">
                    <span className="sr-only">Étape {e.cle} : </span>
                    {e.titre}
                  </h3>
                  <p className="mt-0.5 text-sm text-encre-2">{QUI_AGIT[e.cle]}</p>
                </div>
              </li>
            ))}
          </ol>
        </Section>

        <Section id="paiement" titre="Avant la demande de paiement">
          <Carte className="p-5">
            <p className="text-sm text-encre-2">L'organisme ne peut émettre la demande de paiement que sur un dossier <strong className="text-encre">complet</strong>. Sur la fiche du dossier, la liste « Avant la demande de paiement » coche ce qui est acquis et dit ce qui manque encore, apprenant par apprenant :</p>
            <ul className="mt-3 grid gap-1.5 text-sm sm:grid-cols-2">
              {["Formation déclarée terminée", ...PIECES_REQUISES, "Votre facture réelle, déposée sur le dossier (onglet « Communication avec l'OF »)"].map((x) => (
                <li key={x} className="flex items-start gap-2">
                  <BadgeCheck className="mt-0.5 size-4 shrink-0 text-valide" aria-hidden />
                  {x}
                </li>
              ))}
            </ul>
          </Carte>
        </Section>

        <Section id="generation-ia" titre="Générer vos documents avec l'IA">
          <Carte className="space-y-3 p-5 text-sm leading-relaxed">
            <p>
              <strong>1. Une recherche, une base de sources.</strong> L'analyse des enjeux (ou la première génération du parcours) lance <em>une</em> recherche web approfondie sur le sujet. Elle alimente la <strong>base de sources</strong> de la formation (bouton « Sources de la formation ») : titre, site, date, type (réglementaire, métier, données chiffrées, pédagogie), résumé, extrait. Parcours, tests, supports et visuels s'appuient ensuite sur cette base, sans nouvelle recherche, et la citent (« [S2] »). Vous pouvez ajouter ou retirer une source à la main ; le lien de la page se partage mais ne s'ouvre qu'une fois connecté.
            </p>
            <p>
              <strong>2. Une barre de progression honnête.</strong> Chaque génération tourne sur le serveur ; l'écran affiche le pourcentage, l'étape en cours (« Recherche des sources… », « Rédaction du module 3/6… ») et le temps écoulé. Vous pouvez continuer à travailler pendant ce temps.
            </p>
            <p>
              <strong>3. Trois essais, puis un document figé.</strong> Pour un même document (parcours, test de positionnement, évaluation des acquis, support d'un module, visuels d'un module), vous disposez de <strong>3 générations</strong> avant d'enregistrer (« 2 essais restants »). Dès que vous enregistrez le document généré, sa génération est <strong>verrouillée</strong> : « Document produit le … — génération verrouillée ». Vous le modifiez toujours à la main. En cas de besoin réel, l'administrateur de l'organisme peut rouvrir la génération (motif obligatoire, conservé au journal).
            </p>
            <p>
              <strong>4. Des visuels pour faciliter la compréhension.</strong> L'agent « visualisation des savoirs » propose, pour chaque module, 1 à 3 visuels adaptés à la notion : processus, cycle, frise, pyramide, matrice 2 × 2, tableau comparatif, carte conceptuelle, graphique. Un graphique chiffré n'est produit que si ses chiffres figurent dans un extrait de la base de sources, cité sous le graphique. Ils sont illustrés par Gemini (le texte reste posé par la plateforme, sans faute) ou dessinés en schéma vectoriel, dans le style choisi (« Style de ce support »). Validez-les : ils sont placés dans les emplacements prévus du gabarit du support PPTX et se téléchargent en PNG ou SVG.
            </p>
          </Carte>
        </Section>

        <Section id="profil" titre="Votre profil et vos pièces">
          <Carte className="p-5">
            <p className="text-sm text-encre-2">
              Votre profil (menu « Mon profil ») réunit vos coordonnées, votre <strong className="text-encre">photo</strong> (elle apparaît à côté de votre nom pour qu'on vous reconnaisse ; elle se change aussi dans « Mon compte »), votre structure, votre <strong className="text-encre">domaine d'expertise</strong> choisi dans un menu (les catégories du catalogue ; « Autre » pour préciser), votre profil LinkedIn et le site de votre structure.
            </p>
            <p className="mt-3 text-sm text-encre-2">L'onglet « Mes pièces » liste chaque pièce avec sa date de dépôt : vous pouvez l'ouvrir, la télécharger ou la remplacer. Sont <strong className="text-encre">obligatoires</strong> :</p>
            <ul className="mt-2 grid gap-1.5 text-sm sm:grid-cols-2">
              {["Curriculum vitæ", "Parcours de formation (programme)", "Déroulé pédagogique", "Récépissé de déclaration d'activité (NDA) — sauf si vous cochez « Je n'ai pas de NDA »", "Pièce d'identité", "Diplômes et titres"].map((x) => (
                <li key={x} className="flex items-start gap-2">
                  <BadgeCheck className="mt-0.5 size-4 shrink-0 text-valide" aria-hidden />
                  {x}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-sm text-encre-2">PDF, Word ou image, 15 Mo au plus ; les pièces sont conservées chiffrées. Tant qu'une pièce manque, un bandeau « Pièces à déposer » s'affiche et l'organisme ne peut pas valider une candidature ; un compte déjà validé garde tous ses accès.</p>
          </Carte>
        </Section>

        <Section id="cpf" titre="Financement : CPF, FAFCEA, FIFPL, OPCO">
          <Carte className="p-5">
            <p className="text-sm text-encre-2">
              Depuis le 04/11/2026, <strong className="text-encre">l'organisme prépare et suit lui-même les demandes de financement</strong> de vos dossiers (carte « Demandes de financement » de la fiche dossier) : CPF formation et bilan de compétences (dossier EDOF), et, selon l'organisme, FAFCEA, FIFPL et OPCO. Ce service est inclus et n'est pas facturé.
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-[13px] text-encre-2">
              <li>Hors CPF, le client signe d'abord un <strong className="text-encre">mandat</strong> (dans son espace, ou sur papier) : rien n'est déposé sans lui.</li>
              <li>FAFCEA : le formulaire officiel est pré-rempli puis <strong className="text-encre">signé à la main</strong> par le stagiaire ; le dossier complet doit être reçu entre trois mois avant et le jour du début.</li>
              <li>CPF : les pièces suivent les dates de session EDOF ; après la formation, transmettez sans tarder vos documents de clôture (sortie et service fait sont déclarés dès le lendemain de la fin).</li>
              <li>Bilan de compétences : vous signez l'<strong className="text-encre">engagement de confidentialité</strong> avant le bénéficiaire ; l'entrée en formation ne peut pas être déclarée sans les trois signatures.</li>
            </ul>
            <p className="mt-2 text-[13px] text-encre-2">Si l'organisme a désactivé le CPF, le bouton « Transmettre la demande CPF à Formatrix » reprend sa place sur la fiche du dossier.</p>
          </Carte>
        </Section>

        <Section id="publication" titre="Publier une formation sur skills4mation.com">
          <Carte className="p-5">
            <p className="text-sm text-encre-2">
              Une fois le parcours d'une formation généré et enregistré, la carte « Publication sur le site » de sa fiche propose <strong className="text-encre">« Demander la publication sur skills4mation.com »</strong>. Choisissez le visuel de la fiche : généré par l'IA (photo réaliste, sans texte ni visage reconnaissable, 3 essais par demande), repris de la bibliothèque des visuels du site, ou téléversé (JPG, PNG ou WebP, 5 Mo). Relisez son texte alternatif, ajoutez un message si besoin, puis envoyez.
            </p>
            <p className="mt-2 text-[13px] text-encre-2">L'organisme accepte ou refuse (avec un motif) ; vous êtes prévenu par e-mail et le statut s'affiche sur la formation. Après acceptation, la fiche est mise en ligne par l'équipe du site : publication sous 48 h après validation. Après un refus, aménagez la formation et renouvelez la demande.</p>
          </Carte>
        </Section>

        <Section id="veille" titre="Veille réglementaire de l'organisme">
          <VeilleDiffusee />
        </Section>

        <Section id="qualite" titre="Qualiopi et traçabilité">
          <Carte className="flex gap-4 p-5">
            <ShieldCheck className="mt-0.5 size-5 shrink-0 text-accent" aria-hidden />
            <div className="space-y-2">
            <p className="text-sm text-encre-2">Chaque pièce a un statut simple : « En attente de retour » ou « Validé ». Les signatures en ligne sont horodatées par le serveur et scellées par une empreinte ; le journal du dossier garde la trace de chaque action. Vous n'avez rien à archiver vous-même : tout est rangé dans le dossier, prêt pour un audit.</p>
            {r && (
              <p className="text-sm text-encre-2">
                <strong className="text-encre">Apprenant en situation de handicap (indicateur 26)</strong> : notez son besoin sur sa fiche, puis adressez-le{r.referent_handicap.nom ? ` au référent handicap de l'organisme, ${r.referent_handicap.nom}` : " à l'organisme"}{r.referent_handicap.email ? ` (${r.referent_handicap.email}${r.referent_handicap.telephone ? `, ${r.referent_handicap.telephone}` : ""})` : ""}, qui étudie l'adaptation de la formation. Ses coordonnées figurent aussi sur la convocation.
              </p>
            )}
            </div>
          </Carte>
        </Section>
      </div>
    </div>
  );
}

/** Lot 2.3 : les informations de veille diffusées par l'organisme (indicateur 23 : la veille est exploitée ET partagée). */
function VeilleDiffusee() {
  const veille = useQuery({ queryKey: ["veille", "diffusee"], queryFn: () => api.get<LigneVeille[]>("/veille") });
  if (veille.isPending) return <p className="text-sm text-encre-2">Chargement…</p>;
  if (veille.error) return <p className="text-sm text-danger">{veille.error.message}</p>;
  const actives = veille.data.filter((l) => l.diffusee !== false && !l.archive_le).slice(0, 6);
  if (actives.length === 0) return <p className="text-sm text-encre-2">Aucune information diffusée pour le moment.</p>;
  return (
    <ul className="space-y-3">
      {actives.map((l) => (
        <li key={l.id}>
          <FicheVeille l={l} />
        </li>
      ))}
    </ul>
  );
}
